import React, { useEffect, useState } from 'react';
import { animalApi, feedingApi, inventoryApi, masterDataApi } from '../services/api';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';
import { Box, Animal } from '../types';
import AnimalCard from './AnimalCard';

interface BoxDetailsProps {
  box: Box;
  onClose: () => void;
}

interface Feeding {
  id: number;
  feed: { id: number; name: string };
  quantity: number;
  quantity_unit: string;
  per_animal_quantity: number;
  animals_count: number;
  fed_at: string;
  notes?: string;
}

const BoxDetails: React.FC<BoxDetailsProps> = ({ box, onClose }) => {
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [feedings, setFeedings] = useState<Feeding[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFeedingModal, setShowFeedingModal] = useState(false);
  const [availableFeeds, setAvailableFeeds] = useState<any[]>([]);
  const [feedQuantityUnits, setFeedQuantityUnits] = useState<any[]>([]);
  const [selectedAnimalId, setSelectedAnimalId] = useState<number | null>(null);
  const [newFeeding, setNewFeeding] = useState({
    feed_inventory_id: '',
    quantity: '',
    quantity_unit: '',
    fed_at: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    loadBoxData();
    loadMasterData();
  }, [box.id]);

  const loadBoxData = async () => {
    try {
      setLoading(true);
      const [animalsResponse, feedingsResponse] = await Promise.all([
        animalApi.getAll({ box_id: box.id }),
        feedingApi.getBoxFeedings(box.id)
      ]);
      
      setAnimals(animalsResponse.data || []);
      setFeedings(Array.isArray(feedingsResponse) ? feedingsResponse : []);
    } catch (error) {
      console.error('Error loading box data:', error);
      handleApiError(error, 'Hiba történt a box adatainak betöltésekor');
      setAnimals([]);
      setFeedings([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMasterData = async () => {
    const farmId = box.stable?.farm_id;
    try {
      const [feedsInStock, feedUnitsCategory] = await Promise.all([
        farmId ? inventoryApi.getFeeds(farmId, true) : Promise.resolve([]),
        masterDataApi.getCategory('quantity_units')
      ]);

      setAvailableFeeds(feedsInStock);
      setFeedQuantityUnits(feedUnitsCategory.values || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      handleApiError(error, 'Hiba történt a törzsadatok betöltésekor');
    }
  };

  const handleAddFeeding = async () => {
    try {
      if (!newFeeding.feed_inventory_id || !newFeeding.quantity) {
        showError('Kérlek töltsd ki a kötelező mezőket!');
        return;
      }

      if (animals.length === 0) {
        showError('A box nem tartalmaz állatokat!');
        return;
      }

      // Check if enough feed is in stock
      const selectedFeed = availableFeeds.find((feed: any) =>
        feed.id.toString() === newFeeding.feed_inventory_id
      );

      if (selectedFeed && parseFloat(newFeeding.quantity) > selectedFeed.quantity) {
        showError(`Nincs elég takarmány készleten! Elérhető: ${selectedFeed.quantity} ${selectedFeed.quantity_unit}`);
        return;
      }

      const feedingData = {
        farm_id: box.stable?.farm_id,
        feed_inventory_id: parseInt(newFeeding.feed_inventory_id),
        box_id: box.id,
        feeding_type: 'box',
        quantity: parseFloat(newFeeding.quantity),
        fed_at: newFeeding.fed_at,
        notes: newFeeding.notes
      };

      await feedingApi.create(feedingData);
      showSuccess('Takarmányozás sikeresen rögzítve!');
      
      setShowFeedingModal(false);
      setNewFeeding({
        feed_inventory_id: '',
        quantity: '',
        quantity_unit: '',
        fed_at: new Date().toISOString().split('T')[0],
        notes: ''
      });
      
      loadBoxData();
    } catch (error: any) {
      console.error('Error adding feeding:', error);
      handleApiError(error, 'Hiba történt a takarmányozás rögzítése során');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aktiv':
        return 'bg-green-100 text-green-800';
      case 'eladott':
        return 'bg-blue-100 text-blue-800';
      case 'levagott':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white mb-10">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">
            Box részletek - {box.name}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Box információk */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Box adatok</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Név:</span>
                <span className="font-medium">{box.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Kapacitás:</span>
                <span className="font-medium">{box.capacity} férőhely</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Jelenlegi állatszám:</span>
                <span className="font-medium">{animals.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Szabad helyek:</span>
                <span className={`font-medium ${(box.capacity - animals.length) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {box.capacity - animals.length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Kihasználtság:</span>
                <span className="font-medium">
                  {box.capacity > 0 ? Math.round((animals.length / box.capacity) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>

          {/* Gyors statisztikák */}
          <div className="bg-green-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Gyors áttekintés</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Hím állatok:</span>
                <span className="font-medium">{animals.filter(a => a.gender === 'him').length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Nőstény állatok:</span>
                <span className="font-medium">{animals.filter(a => a.gender === 'nosteny').length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Aktív állatok:</span>
                <span className="font-medium">{animals.filter(a => a.status === 'aktiv').length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Vágható állatok:</span>
                <span className="font-medium text-green-600">
                  {animals.filter(a => a.can_be_slaughtered).length}
                </span>
              </div>
            </div>
          </div>

          {/* Súly információk */}
          <div className="bg-yellow-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Súly adatok</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Átlagos súly:</span>
                <span className="font-medium">
                  {animals.length > 0 
                    ? `${Math.round(animals.reduce((sum, a) => sum + (a.estimated_current_weight || 0), 0) / animals.length)} kg`
                    : '-'
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Összes súly:</span>
                <span className="font-medium">
                  {Math.round(animals.reduce((sum, a) => sum + (a.estimated_current_weight || 0), 0))} kg
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Legkönnyebb:</span>
                <span className="font-medium">
                  {animals.length > 0 
                    ? `${Math.min(...animals.map(a => a.estimated_current_weight || 0))} kg`
                    : '-'
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Legnehezebb:</span>
                <span className="font-medium">
                  {animals.length > 0 
                    ? `${Math.max(...animals.map(a => a.estimated_current_weight || 0))} kg`
                    : '-'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Állatok listája */}
        <div className="mt-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Boxban lévő állatok ({animals.length})
          </h4>
          {animals.length > 0 ? (
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fülszám</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fajta</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nem</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Születés</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Súly</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Státusz</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vágható</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {animals.map((animal) => (
                    <tr key={animal.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedAnimalId(animal.id)}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline">
                          {animal.ear_tag}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{animal.breed}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {animal.gender === 'him' ? 'Hím' : 'Nőstény'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(animal.birth_date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {animal.estimated_current_weight ? `${animal.estimated_current_weight} kg` : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(animal.status)}`}>
                          {animal.status.charAt(0).toUpperCase() + animal.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {animal.can_be_slaughtered ? (
                          <span className="text-green-600 font-medium">Igen</span>
                        ) : (
                          <span className="text-red-600">Nem</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M34 40h10v-4a6 6 0 00-10.712-3.714M34 40H14m20 0v-4a9.971 9.971 0 00-.712-3.714M14 40H4v-4a6 6 0 0110.713-3.714M14 40v-4c0-1.313.253-2.566.713-3.714m0 0A9.971 9.971 0 0124 24c4.21 0 7.863 2.613 9.287 6.286" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nincs állat ebben a boxban</h3>
              <p className="mt-1 text-sm text-gray-500">Ez a box jelenleg üres.</p>
            </div>
          )}
        </div>

        {/* Takarmányozási rész */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">
              Takarmányozási előzmények ({feedings.length})
            </h4>
            <button
              onClick={() => setShowFeedingModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center gap-2"
              disabled={animals.length === 0}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Box takarmányozása
            </button>
          </div>

          {feedings.length > 0 ? (
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dátum</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Takarmány</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Össz mennyiség</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Állat/mennyiség</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Állatok száma</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Megjegyzés</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {feedings.map((feeding) => (
                    <tr key={feeding.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(feeding.fed_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {feeding.feed?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {feeding.quantity} {feeding.quantity_unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        {feeding.per_animal_quantity} {feeding.quantity_unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {feeding.animals_count} állat
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {feeding.notes || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">Még nincsenek takarmányozási előzmények rögzítve.</p>
            </div>
          )}
        </div>

        {/* Takarmányozás hozzáadása modal */}
        {showFeedingModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Box takarmányozása</h3>
                <button
                  onClick={() => setShowFeedingModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Box:</strong> {box.name}<br/>
                    <strong>Állatok száma:</strong> {animals.length}<br/>
                    <em>Az össz mennyiség automatikusan el lesz osztva az állatok között.</em>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Takarmány (készleten)</label>
                  <select
                    value={newFeeding.feed_inventory_id}
                    onChange={(e) => {
                      const selected = availableFeeds.find((f: any) => f.id.toString() === e.target.value);
                      setNewFeeding({
                        ...newFeeding,
                        feed_inventory_id: e.target.value,
                        quantity_unit: selected ? selected.quantity_unit : ''
                      });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  >
                    <option value="">Válassz takarmányt...</option>
                    {availableFeeds.map((feedItem: any) => (
                      <option key={feedItem.id} value={feedItem.id}>
                        {feedItem.display_name || feedItem.feed?.name} - Készlet: {feedItem.quantity} {feedItem.quantity_unit}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Össz mennyiség</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newFeeding.quantity}
                      onChange={(e) => setNewFeeding({...newFeeding, quantity: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Egység</label>
                    <input
                      type="text"
                      value={newFeeding.quantity_unit}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 cursor-not-allowed"
                      disabled
                      placeholder="Automatikus"
                    />
                  </div>
                </div>

                {newFeeding.quantity && animals.length > 0 && (
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-sm text-green-700">
                      <strong>Mennyiség állatonként:</strong> {(parseFloat(newFeeding.quantity) / animals.length).toFixed(2)} {newFeeding.quantity_unit}
                    </p>
                  </div>
                )}

                {newFeeding.feed_inventory_id && newFeeding.quantity && (
                  (() => {
                    const selectedFeed = availableFeeds.find((feed: any) =>
                      feed.id.toString() === newFeeding.feed_inventory_id
                    );
                    
                    if (!selectedFeed) return null;
                    
                    const requestedQuantity = parseFloat(newFeeding.quantity);
                    const availableQuantity = selectedFeed.quantity;
                    
                    if (requestedQuantity > availableQuantity) {
                      return (
                        <div className="bg-red-50 p-3 rounded-lg">
                          <p className="text-sm text-red-700">
                            ⚠️ <strong>Nincs elég készlet!</strong> Elérhető: {availableQuantity} {selectedFeed.quantity_unit}
                          </p>
                        </div>
                      );
                    } else if (requestedQuantity > availableQuantity * 0.8) {
                      return (
                        <div className="bg-yellow-50 p-3 rounded-lg">
                          <p className="text-sm text-yellow-700">
                            ⚠️ <strong>Alacsony készlet!</strong> Maradék: {(availableQuantity - requestedQuantity).toFixed(2)} {selectedFeed.quantity_unit}
                          </p>
                        </div>
                      );
                    }
                    
                    return null;
                  })()
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Takarmányozás dátuma</label>
                  <input
                    type="date"
                    value={newFeeding.fed_at}
                    onChange={(e) => setNewFeeding({...newFeeding, fed_at: e.target.value})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Megjegyzés</label>
                  <textarea
                    value={newFeeding.notes}
                    onChange={(e) => setNewFeeding({...newFeeding, notes: e.target.value})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleAddFeeding}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                >
                  Takarmányozás rögzítése
                </button>
                <button
                  onClick={() => setShowFeedingModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Mégse
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Animal Card Modal */}
        {selectedAnimalId && (
          <AnimalCard
            animalId={selectedAnimalId}
            onClose={() => setSelectedAnimalId(null)}
          />
        )}
      </div>
    </div>
  );
};

export default BoxDetails;